import { Fragment, useEffect, useRef, useState, type ReactNode } from 'react';
import type { Crumb, SwitchOption } from './node-tree-path.js';

/*
 * 两个雪佛龙，方向不同，因为它们说的是两件事：
 *
 * - 朝右的是**路径分隔符**「A › B」。它同时是 A 那一层的切换器，但形状仍按分隔符来——
 *   朝下的雪佛龙插在两段名字中间会把路径读断。能点这件事由悬停和光标说。
 * - 朝下的是**打开菜单**。只有当前段末尾那一个是它，那儿没有下一段，不存在读断的问题。
 */
const SEP = (
  <svg width="8" height="8" viewBox="0 0 8 8" aria-hidden="true">
    <path
      d="M3 1.5 L5.5 4 L3 6.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** 加号。尺寸笔画都跟上面那两个雪佛龙一样——它们在同一行里并排，粗细不一致就看着歪。 */
const PLUS = (
  <svg width="9" height="9" viewBox="0 0 10 10" aria-hidden="true">
    <g fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
      <path d="M5 1.8 V8.2" />
      <path d="M1.8 5 H8.2" />
    </g>
  </svg>
);

const CARET = (
  <svg width="8" height="8" viewBox="0 0 8 8" aria-hidden="true">
    <path
      d="M1.5 3 L4 5.5 L6.5 3"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const GLASS = (
  <svg width="13" height="13" viewBox="0 0 15 15" aria-hidden="true">
    <g fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round">
      <circle cx="6.6" cy="6.6" r="4.4" />
      <path d="M10 10 L13.2 13.2" />
    </g>
  </svg>
);

/** 超过这个条数才给筛选框。 */
const FILTER_AT = 12;

type Group = { title: string; rows: SwitchOption[] };

/** 浮层挂在哪儿。面包屑那一条自己会被裁，所以用固定定位，位置在打开时按折角算。 */
type Anchor = { key: string; left: number; top: number; groups: Group[]; label: string };

/**
 * 一层的切换器：那一段的折角点开的东西。
 *
 * 分组是「同级 / 这一站的 ATC / 下级组织」——切换器要回答的就是这三种「换到哪儿」。
 * 空的组不画。段头带条数，因为「这儿有几个」是人决定要不要点进去的依据。
 *
 * 整条路径上只有这一个浮层组件——每一段的折角开的都是它，内容由那一段是谁算出来。
 */
function Switcher({
  anchor,
  onPick,
  onClose,
}: {
  anchor: Anchor;
  onPick: (id: string) => void;
  onClose: () => void;
}): ReactNode {
  const box = useRef<HTMLDivElement | null>(null);
  const [query, setQuery] = useState('');
  const [at, setAt] = useState(0);

  const total = anchor.groups.reduce((sum, group) => sum + group.rows.length, 0);
  const needle = query.trim().toLowerCase();

  const shown = anchor.groups
    .map((group) => ({
      title: group.title,
      rows: group.rows.filter((row) => needle === '' || row.name.toLowerCase().includes(needle)),
    }))
    .filter((group) => group.rows.length > 0);

  /** 摊平成一列，键盘就在这一列上走——↑↓ 要能跨过分组的段头。 */
  const flat = shown.flatMap((group) => group.rows);
  const active = Math.min(at, Math.max(0, flat.length - 1));

  /*
   * 收起：Esc，或者点到浮层以外。
   *
   * 不用 useDismissOnOutside：那个钩子会把「点触发它的那个折角」也算成点在外面，
   * 于是折角的 click 紧接着又把它打开一次，看起来就是关不掉。这儿要把折角排除掉，
   * 所以自己写一份——判断依据是 data-crumb-switch 这个标记。
   */
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    const onPointerDown = (event: PointerEvent): void => {
      const target = event.target as HTMLElement;
      if (box.current?.contains(target) === true) return;
      if (target.closest('[data-crumb-switch]') !== null) return;
      onClose();
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('pointerdown', onPointerDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('pointerdown', onPointerDown);
    };
  }, [onClose]);

  /** 键盘选中的那一项滚进视野，只滚必要的距离。 */
  useEffect(() => {
    box.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  function onKeyDown(event: React.KeyboardEvent): void {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setAt(Math.min(active + 1, flat.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setAt(Math.max(active - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const row = flat[active];
      if (row !== undefined) onPick(row.id);
    }
  }

  let slot = -1;

  return (
    <div
      className="crumb-switch"
      ref={box}
      role="dialog"
      aria-label={anchor.label}
      style={{ left: anchor.left, top: anchor.top }}
      onKeyDown={onKeyDown}
    >
      {/*
        条数少的时候不给筛选框：几个选项配一个搜索框是在羞辱用户。
        门槛按能装下的项数定——菜单最高 320px，一项 30px，减掉段头正好八九项。
      */}
      {total > FILTER_AT && (
        <div className="cs-filter">
          <span aria-hidden="true">{GLASS}</span>
          <input
            autoFocus
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setAt(0);
            }}
            placeholder="筛这一层"
            aria-label="筛这一层"
          />
        </div>
      )}

      <div className="cs-scroll">
        {shown.length === 0 ? (
          <p className="cs-empty">这一层没有匹配的。</p>
        ) : (
          shown.map((group, index) => (
            <Fragment key={group.title}>
              {index > 0 && <div className="cs-div" />}
              <div className="cs-head">
                <span>{group.title}</span>
                <span className="cs-n">{group.rows.length}</span>
              </div>
              {group.rows.map((row) => {
                slot += 1;
                const mine = slot;
                return (
                  <button
                    key={row.id}
                    type="button"
                    className="cs-item"
                    aria-selected={mine === active}
                    // 鼠标滑过就把键盘选中挪过来，永远只有一行是亮的
                    onMouseEnter={() => setAt(mine)}
                    onClick={() => onPick(row.id)}
                  >
                    <span className="cs-check">{row.here ? '✓' : ''}</span>
                    {row.typeName !== null && <span className="node-type">{row.typeName}</span>}
                    <span className="cs-name">{row.name}</span>
                  </button>
                );
              })}
            </Fragment>
          ))
        )}
      </div>

      {/* 筛掉了多少要说出来，否则「就这么几条」和「筛剩这几条」看起来一样。 */}
      {total > FILTER_AT && (
        <p className="cs-foot">
          这一层一共 {total} 条，列出 {flat.length} 条
        </p>
      )}
    </div>
  );
}

/**
 * 路径条：**一串层级切换器**。
 *
 * 每一段不是一个标签，是那一层的切换器——名字点了去那一层，滑过露出的折角点了换一个。
 * 于是整条路径只有一种元素和一个浮层组件，而不是「面包屑一套 + 下拉另一套」。
 *
 * 三条规则值得写下来，因为它们各自替掉了一个旧行为：
 *
 * - **当前段不是按钮。** 它是一段文字加类型标签。原来它是深墨 600 的按钮，于是视觉上最重
 *   的那一段恰恰是唯一点了没反应的，信号是反的。但它的折角**常驻**——从当前位置往下钻是
 *   这一行最常用的动作，藏起来等于藏掉主功能。
 * - **放不下的时候，先挤祖先，不动当前段。** 祖先可以缩到只剩两三个字加省略号，
 *   当前段一律完整——它是这一行的主语。这一版明确推翻代码里原来那句
 *   「名字一律完整显示，路径长了让这一条横向滚动」：横向滚动的代价是当前节点在最右边、
 *   第一个被滚出视野。
 *
 *   **这件事全部交给 flex，一行 JS 都不用。** 想过按实际宽度把中间几段折成一个「…」
 *   （原型里就是那样），但那需要「渲染 → 测量 → 改状态 → 再渲染」的级联，而收益只是
 *   把「生…」换成「…」。挤压在原地反而更好：每一层都还在它原来的位置上，
 *   还留着头几个字，而且完整的名字点开它的折角就有。
 * - **分隔符「›」就是那一层的切换器**，不另给每段一个折角。
 *
 *   原来每段名字后面跟一个滑过才露的折角，那是照树行上那两个动作抄的——抄错了：树里那两个
 *   按钮在整行的右端，行是满宽的，藏起来时留着的空位不花任何代价；而路径条是横排，
 *   `opacity: 0` 不腾出空间，于是**每一段都为一个看不见的按钮占着 17px**，整行到处是空隙。
 *
 *   分隔符本来就在那儿、本来就占着位置，而且「A › B」里那个 › 读起来就是「A 里面」——
 *   正好是切换器要列的东西。于是零额外宽度，每个元素一件事：名字去那一层，› 换一个。
 *   触屏上也不用特殊照顾了，没有「藏起来的东西摸不到」这回事。
 *
 * - **不跟侧栏的收起状态挂钩。** 侧栏展开时它也有用，尤其是要跳到一份 ATC：
 *   ATC 不在树上，只有这儿能钻进去。
 */
export function Breadcrumbs({
  crumbs,
  onSelect,
  tail,
  onCreate,
}: {
  crumbs: Crumb[];
  onSelect: (id: string) => void;
  /**
   * 在当前这一段下面建一份 ATC。不给就不渲染那个按钮。
   *
   * 传的是回调不是 JSX：这个按钮长什么样是路径条的事（它要跟折角一模一样），
   * 「现在给不给」是外层的事（权限、类型规则、是不是正在新建）。
   */
  onCreate?: () => void;
  /**
   * 末尾再加一段纯文字，不可点。新建页用它（「新建 ATC」）——
   * 那份草稿还没有 id，做不成一段可点的，但路径上必须看得见它。
   */
  tail?: string;
}): ReactNode {
  const [anchor, setAnchor] = useState<Anchor | null>(null);

  const open = (key: string, trigger: HTMLElement, groups: Group[], label: string): void => {
    if (anchor?.key === key) {
      setAnchor(null);
      return;
    }
    const rect = trigger.getBoundingClientRect();
    // 贴着右边界时往左挪，别让浮层跑出窗口
    const left = Math.max(8, Math.min(rect.left - 6, window.innerWidth - 288));
    setAnchor({ key, left, top: rect.bottom + 5, groups, label });
  };

  const groupsOf = (crumb: Crumb): Group[] =>
    [
      { title: '同级', rows: crumb.siblings },
      { title: '这一站的 ATC', rows: crumb.atcs },
      { title: '下级组织', rows: crumb.orgs },
    ].filter((group) => group.rows.length > 0);

  /**
   * 分隔符。左边那一段有东西可切就做成按钮，没有就是个纯符号。
   *
   * 「A › B」里的 › 读起来是「A 里面」，所以它开的是 **A** 的切换器。
   */
  const separator = (before: Crumb | undefined): ReactNode => {
    const groups = before === undefined ? [] : groupsOf(before);
    // 一组都没有就不做按钮：点开是个空浮层，比不能点更让人费解。
    if (before === undefined || groups.length === 0) {
      return (
        <span className="crumb-sep" aria-hidden="true">
          {SEP}
        </span>
      );
    }
    return (
      <button
        type="button"
        className="crumb-sep on"
        data-crumb-switch
        aria-haspopup="dialog"
        aria-expanded={anchor?.key === before.id}
        aria-label={`切换「${before.name}」这一层`}
        onClick={(event) => open(before.id, event.currentTarget, groups, before.name)}
      >
        {SEP}
      </button>
    );
  };

  /** 当前段末尾那个折角。它后面没有分隔符可用，所以自己带一个，而且一直显示。 */
  const endCaret = (crumb: Crumb): ReactNode => {
    const groups = groupsOf(crumb);
    if (groups.length === 0) return null;
    return (
      <button
        type="button"
        className="crumb-caret"
        data-crumb-switch
        aria-haspopup="dialog"
        aria-expanded={anchor?.key === crumb.id}
        aria-label={`进入「${crumb.name}」下面的层级`}
        onClick={(event) => open(crumb.id, event.currentTarget, groups, crumb.name)}
      >
        {CARET}
      </button>
    );
  };

  const lastAt = crumbs.length - 1;

  return (
    <>
      <nav className="crumbs" aria-label="节点路径">
        {crumbs.map((crumb, at) => {
          const isLast = at === lastAt && tail === undefined;

          return (
            <Fragment key={crumb.id}>
              {/* 分隔符属于它左边那一段——开的是那一段的切换器。 */}
              {at > 0 && separator(crumbs[at - 1])}

              {isLast ? (
                <span className="crumb-cur">
                  <span className="cc-name">{crumb.name}</span>

                  {/*
                    是一份 ATC 就显示它的编码，**取代**类型名——编码本来就以类型的 code 开头
                    （ATC-EQU-001），一个标签里已经把「是什么」和「是哪一个」都说了，
                    再并排一个「ATC」是重复。

                    组织单元不走这一支（assetCode 为 null），落到下面显示类型名。
                    根也一样：它没有类型记录，但在树上仍然是一种东西，写死「企业」，
                    跟侧栏那一行一致。
                  */}
                  {crumb.assetCode !== null ? (
                    <span className="node-type is-code">{crumb.assetCode}</span>
                  ) : (
                    <span className="node-type">{crumb.typeName ?? '企业'}</span>
                  )}
                  {endCaret(crumb)}
                  {/*
                    创建入口紧跟在折角后面，跟它一模一样的一个小方块，只有加号没有文字。
                    原来它自己占一整行（面包屑下面的动作条），为一个按钮撑出 40 多像素，
                    而它作用的对象就是这一段——挨着它才说得清建到哪儿去。
                  */}
                  {onCreate !== undefined && (
                    <button
                      type="button"
                      className="crumb-add"
                      title={`在「${crumb.name}」下创建 ATC`}
                      aria-label={`在「${crumb.name}」下创建 ATC`}
                      onClick={onCreate}
                    >
                      {PLUS}
                    </button>
                  )}
                </span>
              ) : (
                <span className="crumb-seg">
                  <button type="button" className="crumb-btn" onClick={() => onSelect(crumb.id)}>
                    {crumb.name}
                  </button>
                </span>
              )}
            </Fragment>
          );
        })}

        {tail !== undefined && (
          <>
            {separator(crumbs[crumbs.length - 1])}
            {/* 草稿没有 id，去不了任何地方，所以是 span 不是 button。 */}
            <span className="crumb-cur">
              <span className="cc-name">{tail}</span>
            </span>
          </>
        )}
      </nav>

      {anchor !== null && (
        <Switcher
          anchor={anchor}
          onPick={(id) => {
            setAnchor(null);
            onSelect(id);
          }}
          onClose={() => setAnchor(null)}
        />
      )}
    </>
  );
}
